import React from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import "./styles/theme-tokens.css";
import App from "./App.jsx";
import "@vscode/codicons/dist/codicon.css";
import {
  applyRuntimeConfig,
  applyViteEnvOverrides,
  applyUrlParamOverrides,
  parseAllUrls,
} from "./data/Services/settings";
import errorReporter from "./utils/errorReportService";
import { createWorkspaceSnapshot } from "./workspace/core/serialization";
// Performance monitor - exposes window.perfMon for console debugging
import "./utils/performanceMonitor";

import { installSafeAreaInsetTracking } from "./utils/safeAreaInsets";

// iOS paisagem: recua o app só no lado do notch (ver safeAreaInsets.js)
installSafeAreaInsetTracking();

// Android: Chrome não gera eventos HTML5 drag-and-drop a partir de touch, e o
// flexlayout arrasta tabs exclusivamente via draggable/dragstart — sem isto,
// mover uma tab entre sections é impossível em tablet Android (o resize do
// splitter funciona porque usa pointer events). O polyfill converte o gesto de
// touch em eventos DnD sintéticos. Só é carregado em dispositivos de toque;
// intercepta apenas elementos [draggable], então não afeta scroll/pan normais.
if ("ontouchstart" in window || navigator.maxTouchPoints > 0) {
  import("drag-drop-touch");
}

// Inject the workspace-snapshot builder into the error reporter so the reporter
// does NOT import serialization directly (that static edge closed 3 import cycles).
errorReporter.setWorkspaceSnapshotProvider(createWorkspaceSnapshot);

// Suppress noisy React warnings about the legacy contextTypes / childContextTypes APIs in development.
// We keep this local and dev-only so we don't accidentally hide real errors in prod.
if (import.meta.env.MODE !== "production") {
  const suppressedMessages = [
    "uses the legacy contextTypes API",
    "uses the legacy childContextTypes API",
  ];
  const origConsoleError = console.error.bind(console);
  console.error = (...args) => {
    try {
      const first = args[0];
      if (
        typeof first === "string" &&
        suppressedMessages.some((m) => first.includes(m))
      ) {
        return; // swallow this known benign warning
      }
    } catch {
      // ignore any interception errors and forward the original args
    }
    origConsoleError(...args);
  };
}

const root = createRoot(document.getElementById("root"));

// Setup global error handlers
window.addEventListener("error", (event) => {
  errorReporter.report(
    event.error,
    {},
    {
      errorType: "JavaScriptError",
      additionalContext: {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    },
  );
});

window.addEventListener("unhandledrejection", (event) => {
  errorReporter.report(
    event.reason,
    {},
    {
      errorType: "UnhandledPromise",
      additionalContext: {
        promiseRejection: true,
      },
    },
  );
});

// Load runtime config (optional) in both dev and production
// Precedence: defaults → runtime-config.json → Vite env vars (dev only)
// CRITICAL: App must NOT render until config is loaded and URLs are set up
fetch("/runtime-config.json", { cache: "no-store" })
  .then((r) => (r.ok ? r.json() : null))
  .then((cfg) => {
    if (cfg) applyRuntimeConfig(cfg);
    // In development, Vite env vars have highest priority
    applyViteEnvOverrides();
    // URL param overrides have highest priority (for debugging)
    applyUrlParamOverrides();
    // Parse all URLs for load balancing support
    parseAllUrls();
    // Mark error reporting as ready after config is loaded
    errorReporter.markConfigReady();
    // Render App ONLY after config is loaded
    root.render(<App />);
  })
  .catch(() => {
    // If runtime-config fails to load, still apply Vite overrides in dev
    applyViteEnvOverrides();
    // URL param overrides have highest priority (for debugging)
    applyUrlParamOverrides();
    // Parse all URLs for load balancing support
    parseAllUrls();
    // Mark error reporting as ready even if config load failed
    errorReporter.markConfigReady();
    // Render App even if config load failed (will use defaults)
    root.render(<App />);
  });
